import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Play, Clock, User, Filter, X } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function TrainingCourses() {
  const { t } = useLanguage();
  const [videos, setVideos] = useState([]);
  const [filter, setFilter] = useState("all");
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    axios.get(`${API}/academy/videos`, { withCredentials: true })
      .then(r => setVideos(r.data || [])).catch(() => setVideos([]));
  }, []);

  const categories = useMemo(() => ["all", ...new Set(videos.map(v => v.category))], [videos]);
  const filtered = filter === "all" ? videos : videos.filter(v => v.category === filter);

  return (
    <div className="px-6 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-white tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>{t("academy.training.title")}</h1>
          <p className="text-xs text-[#A0AEC0] mt-1 tracking-widest uppercase">{t("academy.training.subtitle")} · {videos.length}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-[#A0AEC0]" />
          {categories.map(c => (
            <button key={c} onClick={() => setFilter(c)} data-testid={`filter-${c}`}
              className={`px-3 py-1.5 text-xs tracking-wider transition-all ${
                filter === c
                  ? "bg-[#00E5FF] text-black font-bold"
                  : "bg-[#1F242D] text-[#CBD5E1] hover:text-white hover:bg-[#30363D]"
              }`}>
              {c === "all" ? "Tutti" : c}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map(v => (
          <button key={v.id} data-testid="video-card" onClick={() => setPlaying(v)}
            className="group text-left bg-[#0D0F12] border border-white/[0.04] hover:border-[#00E5FF]/40 transition-all overflow-hidden">
            <div className="relative aspect-video flex items-center justify-center overflow-hidden"
              style={{ background: `linear-gradient(135deg, ${v.thumb}15 0%, ${v.thumb}05 100%)` }}>
              <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.04) 0%, transparent 60%)" }} />
              <div className="w-16 h-16 rounded-full flex items-center justify-center backdrop-blur-sm transition-transform group-hover:scale-110"
                style={{ backgroundColor: `${v.thumb}25`, border: `2px solid ${v.thumb}` }}>
                <Play className="w-7 h-7 ml-0.5" style={{ color: v.thumb }} fill={v.thumb} />
              </div>
              <span className="absolute bottom-3 right-3 px-2 py-1 bg-black/70 text-white text-[11px] tracking-wider font-mono flex items-center gap-1">
                <Clock className="w-3 h-3" /> {v.duration}
              </span>
              <span className="absolute top-3 left-3 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold"
                style={{ backgroundColor: v.thumb, color: "black" }}>{v.level}</span>
            </div>
            <div className="p-5">
              <p className="text-[10px] tracking-widest uppercase mb-2" style={{ color: v.thumb }}>{v.category}</p>
              <h3 className="text-base font-bold text-white mb-2 leading-tight" style={{ fontFamily: "Chivo, sans-serif" }}>{v.title}</h3>
              <p className="text-xs text-[#CBD5E1] leading-relaxed mb-4 line-clamp-3">{v.description}</p>
              <div className="flex items-center gap-2 text-xs text-[#A0AEC0]">
                <User className="w-3 h-3" /> {v.instructor}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Modal player */}
      {playing && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-6" onClick={() => setPlaying(null)}>
          <div className="w-full max-w-4xl bg-[#0A0C10] border border-white/[0.04]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.04]">
              <p className="text-[10px] tracking-widest uppercase" style={{ color: playing.thumb }}>{playing.category}</p>
              <button onClick={() => setPlaying(null)} className="text-[#A0AEC0] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="relative aspect-video flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${playing.thumb}20 0%, #050505 100%)` }}>
              <div className="w-24 h-24 rounded-full flex items-center justify-center animate-pulse"
                style={{ backgroundColor: `${playing.thumb}25`, border: `3px solid ${playing.thumb}` }}>
                <Play className="w-10 h-10 ml-1" style={{ color: playing.thumb }} fill={playing.thumb} />
              </div>
              <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs text-[#A0AEC0] tracking-widest uppercase">
                Demo mode · video placeholder
              </p>
            </div>
            <div className="p-6 space-y-3">
              <h2 className="text-2xl font-bold text-white leading-tight" style={{ fontFamily: "Chivo, sans-serif" }}>{playing.title}</h2>
              <div className="flex items-center gap-4 text-xs text-[#CBD5E1]">
                <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{playing.duration}</span>
                <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{playing.instructor}</span>
                <span className="px-2 py-0.5 text-[10px] font-bold uppercase" style={{ backgroundColor: playing.thumb, color: "black" }}>{playing.level}</span>
              </div>
              <p className="text-sm text-[#D1D5DB] leading-relaxed">{playing.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
